import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Gamepad2, ArrowLeft, User, Mail, Crown, LogOut, Shield, Trash2, AlertTriangle, Zap, Sparkles, Layers } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UserAIKeysCard() {
  const [info, setInfo] = useState(null);
  const [oai, setOai] = useState('');
  const [ant, setAnt] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/me/llm-keys`, { withCredentials: true }).then(r => setInfo(r.data)).catch(() => {});
  }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/me/llm-keys`, payload, { withCredentials: true });
      setInfo(r.data); setOai(''); setAnt('');
      toast.success('AI key saved — your AI usage now bills to your key');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!info) return null;
  if (!info.allowed) return null;

  return (
    <div className="project-card p-6 mb-6" data-testid="user-aikeys-card">
      <h2 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
        <Zap className="w-5 h-5 text-[#06B6D4]" /> Your AI Keys
      </h2>
      <p className="text-xs text-zinc-500 mb-4">Optional: bring your own OpenAI or Anthropic key — Copilot, behavior scripts and chat then bill directly to you instead of the platform.</p>
      {[
        { label: 'OpenAI API Key', masked: info.openai_key_masked, val: oai, set: setOai, field: 'openai_key', ph: 'sk-…', testid: 'user-openai-key' },
        { label: 'Anthropic (Claude) API Key', masked: info.anthropic_key_masked, val: ant, set: setAnt, field: 'anthropic_key', ph: 'sk-ant-…', testid: 'user-anthropic-key' },
      ].map(k => (
        <div key={k.field} className="mb-3">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{k.label} {k.masked && <span className="text-emerald-400 normal-case font-mono ml-1">saved: {k.masked}</span>}</label>
          <div className="flex gap-2">
            <input type="password" placeholder={k.masked ? 'Enter a new key to replace' : k.ph} value={k.val}
              onChange={(e) => k.set(e.target.value)} data-testid={k.testid}
              className="flex-1 bg-[#121214] border border-[#1E1E24] rounded-sm px-3 h-9 text-sm text-white font-mono focus:outline-none focus:border-[#06B6D4]" />
            <Button size="sm" disabled={saving || !k.val.trim()} onClick={() => save({ [k.field]: k.val.trim() })}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-9" data-testid={`${k.testid}-save`}>Save</Button>
            {k.masked && (
              <Button size="sm" variant="ghost" disabled={saving} onClick={() => save({ [k.field]: '' })}
                className="text-zinc-500 hover:text-red-400 text-xs h-9" data-testid={`${k.testid}-clear`}>Clear</Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [deleteMode, setDeleteMode] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/usage/me`, { withCredentials: true })
      .then(r => setUsage(r.data))
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error("Type DELETE to confirm");
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, { withCredentials: true });
      toast.success('Account deleted. Redirecting…');
      await logout();
      setTimeout(() => navigate('/'), 800);
    } catch (e) {
      toast.error('Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="settings-page">
      {/* Header */}
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]">
        <div className="max-w-[800px] mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <Gamepad2 className="w-5 h-5 text-[#06B6D4]" />
            <span className="font-bold text-white">Settings</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[800px] mx-auto px-6 py-8">
        {/* Profile Card */}
        <div className="project-card p-6 mb-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5" /> Profile
          </h2>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-sm bg-[#06B6D4]/20 flex items-center justify-center text-2xl font-bold text-[#06B6D4]">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-xl font-bold text-white">{user?.name}</p>
              <p className="text-zinc-400 font-mono text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" /> {user?.email}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-[#121214] border border-[#1E1E24] p-4 rounded-sm">
              <div className="flex items-center gap-2 mb-1 text-zinc-400">
                <Crown className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-mono uppercase tracking-wider">Plan</span>
              </div>
              <p className="font-bold text-white capitalize">{user?.tier || 'Free'}</p>
            </div>
            <div className="bg-[#121214] border border-[#1E1E24] p-4 rounded-sm">
              <div className="flex items-center gap-2 mb-1 text-zinc-400">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-mono uppercase tracking-wider">Role</span>
              </div>
              <p className="font-bold text-white capitalize">{user?.role || 'User'}</p>
            </div>
          </div>
        </div>

        {/* Usage & Quotas */}
        {usage && (
          <div className="project-card p-6 mb-6" data-testid="usage-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#06B6D4]" /> Today's Usage
              </h2>
              <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{usage.date}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-3">
              {[
                { key: 'copilot', label: 'AI Copilot', icon: Sparkles },
                { key: 'ai_generate', label: 'Game Generation', icon: Zap },
                { key: 'asset_gen', label: 'Asset Generation', icon: Layers },
              ].map(({ key, label, icon: Icon }) => {
                const used = usage.usage?.[key] ?? 0;
                const quota = usage.quotas?.[key] ?? 0;
                const pct = quota > 0 ? Math.min(100, (used / quota) * 100) : 0;
                return (
                  <div key={key} className="bg-[#121214] border border-[#1E1E24] p-4 rounded-sm" data-testid={`usage-${key}`}>
                    <div className="flex items-center justify-between mb-2 text-sm">
                      <span className="text-zinc-300 flex items-center gap-2"><Icon className="w-4 h-4 text-[#06B6D4]" /> {label}</span>
                      <span className="font-mono text-white">{used} / {quota}</span>
                    </div>
                    <div className="h-1.5 bg-[#1E1E24] rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#06B6D4] to-purple-400" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              <div className="bg-[#121214] border border-[#1E1E24] p-4 rounded-sm md:col-span-2" data-testid="usage-projects">
                <div className="flex items-center justify-between mb-2 text-sm">
                  <span className="text-zinc-300">Projects</span>
                  <span className="font-mono text-white">
                    {usage.projects?.used ?? 0} / {usage.projects?.limit >= 9999 ? '∞' : usage.projects?.limit}
                  </span>
                </div>
                <div className="h-1.5 bg-[#1E1E24] rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400" style={{ width: `${usage.projects?.limit >= 9999 ? 5 : Math.min(100, (usage.projects?.used / usage.projects?.limit) * 100)}%` }} />
                </div>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500">
              Quotas reset daily at 00:00 UTC. <button onClick={() => navigate('/pricing')} className="text-[#06B6D4] hover:underline">Upgrade</button> for more.
            </p>
          </div>
        )}

        {/* Personal AI Keys (BYOK) */}
        <UserAIKeysCard />

        {/* About */}
        <div className="project-card p-6 mb-6">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Gamepad2 className="w-5 h-5" /> About PlayMold
          </h2>
          
          <div className="text-sm text-zinc-400 space-y-3">
            <p>
              PlayMold is an AI-powered game creation platform that allows anyone to 
              create playable 3D games using natural language prompts.
            </p>
            <p>
              Powered by GPT-5.2 for game logic generation and Babylon.js for real-time 3D rendering.
            </p>
            <div className="bg-[#06B6D4]/10 border border-[#06B6D4]/30 rounded-sm p-3 text-xs">
              <p className="text-[#06B6D4]">
                <strong>Version:</strong> 1.0.0 Beta
              </p>
            </div>
          </div>
        </div>

        {/* Logout */}
        <Button 
          variant="outline"
          className="w-full bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </Button>

        {/* Preferences — touch coach mark replay, chime mute, etc. */}
        <div className="mt-8 rounded-xl border border-[#1E1E24] bg-[#0A0A0B] p-6" data-testid="preferences-card">
          <h3 className="font-bold text-white mb-1">Preferences</h3>
          <p className="text-xs text-zinc-500 mb-4">Small settings you can flip on-the-fly.</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white">Touch tutorial</p>
                <p className="text-xs text-zinc-500">Replay the 3-step gesture coach mark next time you open the Editor.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-[#1E1E24] hover:border-[#06B6D4] hover:text-[#06B6D4]"
                onClick={() => {
                  try { localStorage.removeItem('pm_touch_coachmark_seen_v1'); } catch { /* ignore */ }
                  toast.success('Touch tutorial reset — it\u2019ll show next time you open the editor');
                }}
                data-testid="settings-replay-coachmark"
              >
                Show me again
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white">Editor guided tour</p>
                <p className="text-xs text-zinc-500">Replay the 6-step Editor walkthrough (Undo, Revert, Assets, Library, Export).</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-[#1E1E24] hover:border-[#06B6D4] hover:text-[#06B6D4]"
                onClick={() => {
                  try { localStorage.removeItem('pm_editor_tour_seen_v1'); } catch { /* ignore */ }
                  toast.success('Editor tour reset — it\u2019ll play next time you open the editor');
                }}
                data-testid="settings-replay-editor-tour"
              >
                Show me again
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white">Chime sounds</p>
                <p className="text-xs text-zinc-500">Play a soft chime on generate / publish / send. Toggle from the bottom-left dock too.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-[#1E1E24] hover:border-[#06B6D4] hover:text-[#06B6D4]"
                onClick={() => {
                  try {
                    const cur = localStorage.getItem('pm_chime_muted') === '1';
                    localStorage.setItem('pm_chime_muted', cur ? '0' : '1');
                    toast.success(cur ? 'Chimes unmuted' : 'Chimes muted');
                  } catch { /* ignore */ }
                }}
                data-testid="settings-chime-toggle"
              >
                Toggle
              </Button>
            </div>
          </div>
        </div>

        {/* Danger Zone — Delete Account (Play-Store required data deletion) */}
        <div className="mt-8 rounded-xl border border-red-500/30 bg-red-500/5 p-6" data-testid="danger-zone">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-bold text-red-300">Danger Zone</h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                Permanently delete your account, all projects, and all associated data. This action cannot be undone.
              </p>
            </div>
          </div>

          {!deleteMode ? (
            <Button
              variant="outline"
              className="mt-2 bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10"
              onClick={() => setDeleteMode(true)}
              data-testid="delete-account-init"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete my account
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-zinc-400">
                Type <span className="font-mono text-red-300">DELETE</span> to confirm:
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full bg-[#050505] border border-red-500/40 rounded-sm px-3 py-2 text-white font-mono"
                data-testid="delete-account-confirm-input"
                placeholder="DELETE"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => { setDeleteMode(false); setConfirmText(''); }}
                  className="bg-transparent border-[#1E1E24]"
                  disabled={deleting}
                  data-testid="delete-account-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={confirmText !== 'DELETE' || deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-account-confirm"
                >
                  {deleting ? 'Deleting…' : 'Permanently delete'}
                </Button>
              </div>
            </div>
          )}

          <p className="text-[11px] text-zinc-600 mt-4">
            Read our{' '}
            <Link to="/privacy" className="text-[#06B6D4] hover:underline">Privacy Policy</Link>{' '}
            for details on data deletion.
          </p>
        </div>
      </main>
    </div>
  );
}
